import { useState, useEffect, useCallback } from "react";
import { createContainer } from "unstated-next";

/**
 * Query param manager in a hook. Import `QueryParams` and use with
 * `QueryParams.useContainer()`.
 *
 * The example below updates the browser query params to display the number
 * of times a button is clicked.
 *
 * @example
 * import { QueryParams } from "shared/hooks/useQueryParams";
 *
 * function ExampleComponent() {
 *   const [clicks, setClicks] = useState(0);
 *   const query = QueryParams.useContainer();
 *
 *   return (
 *     <button
 *       onClick={() => {
 *         setClicks(clicks + 1);
 *         query.set("clicks", clicks + 1);
 *       }}>
 *       Increment
 *     </button>
 *   );
 * }
 */
export function useQueryParams() {
  const [params, setParams] = useState({});

  /**
   * Load from browser on mount.
   */
  useEffect(() => {
    const current = new URLSearchParams(window.location.search);
    const obj = {};
    current.forEach((value, key) => {
      obj[key] = value;
    });
    setParams(obj);
  }, []);

  const _updateParams = useCallback(
    (updated) => {
      const _params = new URLSearchParams();

      for (const key in updated) {
        _params.set(key, updated[key]);
      }

      window.history.replaceState(
        null,
        null,
        window.location.pathname + "?" + _params.toString()
      );

      setParams(updated);
    },
    [params, toString]
  );

  return {
    params,

    /**
     * Get a current value by key.
     */
    get: useCallback((key) => params[key], [params]),

    /**
     * Update query params.
     */
    set: useCallback(
      (keyOrObj, value) => {
        if (typeof keyOrObj === "string") {
          _updateParams({
            ...params,
            [keyOrObj]: value,
          });
        } else if (typeof keyOrObj === "object" && !Array.isArray(keyOrObj)) {
          const updated = { ...params };
          for (const key in keyOrObj) {
            updated[key] = keyOrObj[key];
          }
          _updateParams(updated);
        } else {
          throw new Error(
            `First argument should be a string key or an object of keys and values. Received ${typeof keyOrObj}: ${keyOrObj}`
          );
        }
      },
      [params]
    ),
    delete: useCallback((key) => {}),

    toString: useCallback(() => {
      const _params = new URLSearchParams();

      for (const key in params) {
        _params.set(key, params[key]);
      }

      return _params.toString();
    }, [params]),
  };
}

export const QueryParams = createContainer(useQueryParams);
